import React, { useState } from 'react';
import { IconClose, IconSearch } from '../icons';
import { queryParts, removePart, linkedRuns, normalizeQuery, SLOT_LABELS, JAM_LABELS } from './queryOps';
import './search.css';

const SHOW_MAX = 5;

const prettyKey = (k) => String(k || '').replace(/[-_]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

// "Scarlet Begonias (Show opener)" inside a chain label
const chipLabel = (s) => {
  const extra = [];
  if (s.slot && s.slot !== 'any') extra.push(SLOT_LABELS[s.slot]);
  if (s.jam) extra.push('long, ' + String(JAM_LABELS[s.jam] || '').toLowerCase());
  return s.name + (extra.length ? ' (' + extra.join(', ') + ')' : '');
};

// The chips to show: a song that sits in a linked run ("A into B") is shown
// once, inside that run's chip, not again as its own chip. Removing a run
// chip removes its songs.
const chipParts = (query, meta) => {
  const n = normalizeQuery(query);
  const runs = linkedRuns(n);
  const inRun = {};
  runs.forEach((r) => {
    for (let i = r.start; i <= r.end; i++) inRun[i] = true;
  });
  const all = queryParts(n, meta);
  // run chips, in run order (queryParts lists one 'seq' part per linked run)
  const seqs = all.filter((p) => p.kind === 'seq');
  const runChip = (run, i) => ({
    ...(seqs[i] || { kind: 'seq', value: run.keys.join('>') }),
    run,
    label: n.songs
      .slice(run.start, run.end + 1)
      .map(chipLabel)
      .join(' into '),
  });
  const out = [];
  all.forEach((p) => {
    if (p.kind === 'seq') return;
    if (p.kind === 'song' && inRun[p.index]) {
      // the run's chip sits where its first song was
      const ri = runs.findIndex((r) => r.start === p.index);
      if (ri >= 0) out.push(runChip(runs[ri], ri));
      return;
    }
    out.push(p.kind === 'vibe' && p.label === p.value ? { ...p, label: prettyKey(p.value) } : p);
  });
  return { n, parts: out };
};

// Remove every song of a linked run, last first, so the indexes stay valid.
const removeRun = (n, run) => {
  let q = n;
  for (let i = run.end; i >= run.start; i--) q = removePart(q, { kind: 'song', index: i });
  return q;
};

// Header chips for the applied Set Search (replaces the old single chip).
// Removing a chip computes the next query here (queryOps.removePart) and
// hands it to onChange(nextQuery); WEB-CORE runs it, or clears the search
// when queryOps.isEmpty(nextQuery). onClear() clears everything.
// Optional (additive): `meta` supplies era and vibe labels.
export default function ActiveFilterChips({ query, onChange, onClear, meta }) {
  const [expanded, setExpanded] = useState(false);
  if (!query) return null;
  const { n, parts } = chipParts(query, meta);
  if (!parts.length) return null;
  const shown = expanded ? parts : parts.slice(0, SHOW_MAX);
  const hidden = parts.length - shown.length;
  const remove = (p) => (p.run ? removeRun(n, p.run) : removePart(query, p));
  return (
    <div className="ss-afc" role="group" aria-label="Set Search filters">
      <span className="ss-afc-icon" aria-hidden="true">
        <IconSearch size={12} />
      </span>
      {shown.map((p, i) => (
        <button
          key={i + ':' + p.kind + ':' + p.value}
          type="button"
          className={'ss-afc-chip' + (p.kind === 'not' ? ' not' : '')}
          title={'Remove ' + p.label}
          aria-label={'Remove ' + p.label}
          onClick={() => onChange && onChange(remove(p))}
        >
          <span>{p.label}</span>
          <IconClose size={11} />
        </button>
      ))}
      {hidden > 0 && (
        <button type="button" className="ss-afc-chip more" onClick={() => setExpanded(true)} title="Show all filters">
          <span>{hidden} more</span>
        </button>
      )}
      {expanded && parts.length > SHOW_MAX && (
        <button type="button" className="ss-afc-chip more" onClick={() => setExpanded(false)}>
          <span>Fewer</span>
        </button>
      )}
      <button type="button" className="ss-afc-clear" onClick={() => onClear && onClear()} title="Clear Set Search">
        Clear
      </button>
    </div>
  );
}
